import api from "./api";

export const getDashboardStats = async () => {
    const response = await api.get("/crm/dashboard");
    return response.data;
};

export const getVariantStats = async () => {
    const response = await api.get("/crm/variants");
    return response.data;
};

export const getLeads = async () => {
    const response = await api.get("/crm/leads");
    return response.data;
};

export const promoteWinner = async (projectId, variantKey) => {
    const response = await api.post(`/crm/variants/${projectId}/promote`, { variantKey });
    return response.data;
};

export const getDashboardAnalytics = async () => {
    const response = await api.get("/crm/analytics");
    return response.data;
};

export const getProjectAnalytics = async (projectId) => {
    const response = await api.get(`/crm/projects/${projectId}/analytics`);
    return response.data;
};

export const resetVariantStats = async (projectId) => {
    const response = await api.post(`/crm/projects/${projectId}/reset-variant-stats`);
    return response.data;
};

export const archiveChallenger = async (projectId) => {
    const response = await api.post(`/crm/projects/${projectId}/archive-challenger`);
    return response.data;
};

export const getFilteredLeads = async (params = {}) => {
    const response = await api.get("/crm/leads/filter", { params });
    return response.data;
};

export const getVariantPreview = async (projectId, variantKey) => {
    const response = await api.get(`/crm/projects/${projectId}/variants/${variantKey}/preview`);
    return response.data;
};

export const getProjectReport = async (projectId) => {
    const response = await api.get(`/crm/projects/${projectId}/report`);
    return response.data;
};

export const updateLeadStatus = async (leadId, status) => {
    const response = await api.post(`/crm/leads/${leadId}/status`, { status });
    return response.data;
};

export const updateLeadDetails = async (leadId, payload) => {
    const response = await api.post(`/crm/leads/${leadId}/details`, payload);
    return response.data;
};

export const getLeadAudit = async (leadId) => {
    const response = await api.get(`/crm/leads/${leadId}/audit`);
    return response.data;
};

export const exportLeadsCsvUrl = (source, status) => {
    const params = new URLSearchParams();
    if (source) params.append("source", source);
    if (status) params.append("status", status);
    return `${api.defaults.baseURL}/crm/leads/export?${params.toString()}`;
};

export const exportProjectReportUrl = (projectId) => {
    return `${api.defaults.baseURL}/crm/projects/${projectId}/report/export`;
};

export const getLeadKanban = async (ownerName) => {
    const response = await api.get("/crm/leads/kanban", {
        params: ownerName ? { ownerName } : {}
    });
    return response.data;
};

export async function getCrmProjectDetail(projectId) {
    const { data } = await api.get(`/crm/projects/${projectId}`);
    return data;
}

export async function getPrograms(projectId) {
    const { data } = await api.get(`/crm/projects/${projectId}/programs`);
    return data;
}

export async function getProgramById(projectId, programId) {
    const { data } = await api.get(`/crm/projects/${projectId}/programs/${programId}`);
    return data;
}

export async function createProgram(projectId, payload) {
    const { data } = await api.post(`/crm/projects/${projectId}/programs`, payload);
    return data;
}

export async function updateProgram(projectId, programId, payload) {
    const { data } = await api.put(`/crm/projects/${projectId}/programs/${programId}`, payload);
    return data;
}

export async function deleteProgram(projectId, programId) {
    await api.delete(`/crm/projects/${projectId}/programs/${programId}`);
}

/* ---------------- Accounts ---------------- */

export const getAccounts = async () => {
    const response = await api.get("/accounts");
    return response.data;
};

export const createAccount = async (payload) => {
    const response = await api.post("/accounts", payload);
    return response.data;
};

export const assignProjectToAccount = async (projectId, accountId) => {
    const response = await api.post(`/accounts/projects/${projectId}/assign`, {
        accountId
    });
    return response.data;
};

export const getResolvedAccountBranding = async (accountId) => {
    const response = await api.get(`/accounts/${accountId}/branding-resolved`);
    return response.data;
};

export const generateProjectForClient = async (clientAccountId, payload) => {
    const response = await api.post(
        `/accounts/clients/${clientAccountId}/projects/generate`,
        payload
    );

    return response.data;
};

/* ---------------- Modules ---------------- */

export const getModules = async (projectId) => {
    const { data } = await api.get(`/crm/projects/${projectId}/modules`);
    return data;
};

export const getModule = async (projectId, moduleKey) => {
    const { data } = await api.get(`/crm/projects/${projectId}/modules/${moduleKey}`);
    return data;
};

export const updateModuleItems = async (
    projectId,
    moduleKey,
    items,
    options = {}
) => {
    const publish = options.publish !== false;

    const { data } = await api.put(
        `/crm/projects/${projectId}/modules/${moduleKey}/items?publish=${publish}`,
        items
    );

    return data;
};

export const getProgramAnalytics = async (projectId) => {
    const response = await api.get(`/crm/projects/${projectId}/program-analytics`);
    return response.data;
};

export async function regenerateCrmFromSeed(projectId, deleteExistingUserEdited = false) {
    const res = await api.post(
        `/admin/vertical-seeds/projects/${projectId}/regenerate-crm-from-seed?deleteExistingUserEdited=${deleteExistingUserEdited}`
    );

    return res.data;
}

export async function publishCrmProject(projectId) {
    const res = await api.post(`/website/${projectId}/publish`);
    return res.data;
}

export async function redeployProject(projectId, moduleKey = "manual") {
    const res = await api.post(
        `/crm/projects/${projectId}/redeploy?moduleKey=${encodeURIComponent(moduleKey)}`
    );
    return res.data;
}

export const getCrmProjects = async () => {
    const response = await api.get("/crm/projects");
    return response.data;
};

export const getAvailableSeeds = async (projectId) => {
    const res = await api.get(`/admin/vertical-seeds/${projectId}/available-seeds`);
    return res.data;
};

export const regenerateFromSeed = async (projectId, seedId) => {
    const res = await api.post(`/admin/vertical-seeds/projects/${projectId}/regenerate-from-seed/${seedId}`);
    return res.data;
};

export const generateModuleItems = async (projectId, moduleKey, payload = {}) => {
    const body = {
        instruction: payload.instruction || "",
        replaceExisting: payload.replaceExisting ?? false
    };

    // Important:
    // Only send count when caller explicitly provides it.
    // If count is omitted, backend uses crm.defaultSuggestionCount from platform settings.
    if (payload.count !== undefined && payload.count !== null && payload.count !== "") {
        body.count = Number(payload.count);
    }

    const res = await api.post(
        `/crm/projects/${projectId}/modules/${moduleKey}/ai-generate`,
        body
    );

    return res.data;
};

export const fixWebsite = async (projectId) => {
    const res = await api.post(
        `/crm/projects/${projectId}/fix-website`
    );
    return res.data;
};

export const fixWebsiteSection = async (projectId, payload) => {
    const res = await api.post(
        `/crm/projects/${projectId}/ai-fix/section`,
        payload
    );

    return res.data;
};

export const previewWebsiteSectionFix = async (projectId, payload) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/ai-fix/section/preview`,
        payload
    );
    return data;
};

export const applyWebsiteSectionFix = async (projectId, payload) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/ai-fix/section/apply`,
        payload
    );
    return data;
};

export const globalSearch = async (query) => {
    const { data } = await api.get("/crm/search", {
        params: { q: query }
    });

    return data;
};

export const getDeploymentStatus = async (projectId) => {
    const { data } = await api.get(`/crm/projects/${projectId}/deployment-status`);
    return data;
};

export const getProjectOperations = async (
    projectId,
    take = 100
) => {
    const { data } = await api.get(
        `/crm/projects/${projectId}/operations?take=${take}`
    );

    return data;
};

export const markSyncRunFailed = async (
    projectId,
    runId
) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/sync-runs/${runId}/mark-failed`
    );

    return data;
};

export const markRedeployRunFailed = async (
    projectId,
    runId
) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/redeploy-runs/${runId}/mark-failed`
    );

    return data;
};

export async function saveVariantInstruction(projectId, instruction) {
    const { data } = await api.post(
        `/crm/projects/${projectId}/variant-instruction`,
        JSON.stringify(instruction),
        {
            headers: {
                "Content-Type": "application/json"
            }
        }
    );

    return data;
}

export async function regenerateChallenger(projectId) {
    const { data } = await api.post(`/crm/variants/${projectId}/regenerate`);
    return data;
}

export const improveItem = async (projectId, moduleKey, item) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/modules/${moduleKey}/items/ai-improve`,
        item
    );
    return data;
};

export const suggestItemImage = async (projectId, moduleKey, item) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/modules/${moduleKey}/items/suggest-image`,
        item
    );
    return data;
};

export const generateLogoOptions = async (projectId, payload) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/logo-options`,
        payload
    );

    return data;
};

export const saveProjectLogo = async (projectId, payload) => {
    const { data } = await api.post(
        `/crm/projects/${projectId}/save-logo`,
        payload
    );

    return data;
};

export const getBrandingKit = async (projectId, selectedKey) => {
    // We pass the selectedKey so the backend knows which style to base the kit on
    const { data } = await api.get(
        `/crm/projects/${projectId}/branding-kit?selectedKey=${selectedKey}`
    );
    return data;
};

export const getClientBusinessSettings = async () => {
    const { data } = await api.get("/settings/client/business");
    return data;
};

export const saveClientBusinessSettings = async (payload) => {
    const { data } = await api.put("/settings/client/business", payload);
    return data;
};

export const getClientCrmSettings = async () => {
    const { data } = await api.get("/settings/client/crm");
    return data;
};

export const saveClientCrmSettings = async (payload) => {
    const { data } = await api.put("/settings/client/crm", payload);
    return data;
};

export const getClientUsageSettings = async () => {
    const { data } = await api.get("/settings/client/usage");
    return data;
};

export const saveClientUsageSettings = async (payload) => {
    const { data } = await api.put("/settings/client/usage", payload);
    return data;
};
export const getAccountAuditSummary = async (accountId) => {
    const { data } = await api.get(`/accounts/${accountId}/audit-summary`);
    return data;
};

/* ---------------- Platform Settings ---------------- */

export const getPlatformSettings = async () => {
    const { data } = await api.get("/admin/settings");
    return data;
};

export const getPlatformSettingsByPrefix = async (prefix) => {
    const { data } = await api.get("/admin/settings/prefix", {
        params: { prefix }
    });

    return data;
};

export const savePlatformSettings = async (settings) => {
    const { data } = await api.post("/admin/settings/bulk", {
        settings
    });

    return data;
};

export const ensureDefaultPlatformSettings = async () => {
    const { data } = await api.post("/admin/settings/ensure-defaults");
    return data;
};
export const getDeploymentVersions = async (projectId) => {
    const { data } = await api.get(`/crm/projects/${projectId}/deployment-versions`);
    return data;
};

export const rollbackDeploymentVersion = async (projectId, version) => {
    const { data } = await api.post(`/crm/projects/${projectId}/rollback`, {
        version
    });

    return data;
};

export async function getProductionFit(projectId) {
    const { data } = await api.get(`/crm/projects/${projectId}/production-fit`);
    return data;
}

export async function analyzeProductionFit(projectId) {
    const { data } = await api.post(`/crm/projects/${projectId}/production-fit/analyze`);
    return data;
}

export async function getPremiumUpgradePreview(projectId) {
    const { data } = await api.get(`/crm/projects/${projectId}/premium-upgrade/preview`);
    return data;
}

export async function generatePremiumUpgradePreview(projectId) {
    const { data } = await api.post(`/crm/projects/${projectId}/premium-upgrade/preview`);
    return data;
}

export async function applyPremiumUpgrade(projectId, payload = { redeploy: true }) {
    const { data } = await api.post(`/crm/projects/${projectId}/premium-upgrade/apply`, payload);
    return data;
}
export async function fixProductionFitIssues(projectId) {
    const { data } = await api.post(`/crm/projects/${projectId}/production-fit/fix`);
    return data;
}

export const clearGeneratedProjectsDryRun = async (payload = {}) => {
    const response = await api.post("/admin/maintenance/clear-generated-projects", {
        confirmationText: "CLEAR GENERATED PROJECTS",
        scope: payload.scope || "seed-test-only",
        dryRun: true,
        deleteLocalFiles: payload.deleteLocalFiles ?? true,
        deleteAzureFiles: payload.deleteAzureFiles ?? true,
        includeClientOwnedProjects: payload.includeClientOwnedProjects ?? false,
        continueOnAzureFailure: payload.continueOnAzureFailure ?? true,
        orphanSlugs: payload.orphanSlugs || []
    });

    return response.data;
};

export const clearGeneratedProjects = async (payload = {}) => {
    const response = await api.post("/admin/maintenance/clear-generated-projects", {
        confirmationText: payload.confirmationText || "",
        scope: payload.scope || "seed-test-only",
        dryRun: false,
        deleteLocalFiles: payload.deleteLocalFiles ?? true,
        deleteAzureFiles: payload.deleteAzureFiles ?? true,
        includeClientOwnedProjects: payload.includeClientOwnedProjects ?? false,
        continueOnAzureFailure: payload.continueOnAzureFailure ?? true,
        orphanSlugs: payload.orphanSlugs || []
    });

    return response.data;
};

export async function getProjectHealth(projectId) {
    const { data } = await api.get(`/crm/projects/${projectId}/health`);
    return data;
}
export async function repairCrmFromSeed(projectId, payload = {}) {
    const { data } = await api.post(`/crm/projects/${projectId}/repair-crm`, {
        mode: payload.mode || "missing-only",
        queueRedeploy: payload.queueRedeploy !== false
    });

    return data;
}

export const getProjectLeadSummary = async (projectId) => {
    const response = await api.get(`/crm/projects/${projectId}/lead-summary`);
    return response.data;
};

export async function getUnclaimedPreviewSessions(take = 50) {
    const response = await api.get(
        `/admin/preview-sessions/unclaimed?take=${take}`
    );

    return response.data;
}

export async function markPreviewSessionExpired(previewSessionId, reason = "") {
    const response = await api.post(
        `/admin/preview-sessions/${previewSessionId}/mark-expired`,
        { reason }
    );

    return response.data;
}

export async function claimPreviewSessionAsAdmin(previewSessionId) {
    const response = await api.post(
        `/admin/preview-sessions/${previewSessionId}/claim`,
        {}
    );

    return response.data;
}
// Admin job
export async function getAdminJobsConfig() {
    const { data } = await api.get("/admin/jobs/config");
    return data;
}

export async function saveAdminJobsConfig(timing) {
    const { data } = await api.put("/admin/jobs/config", {
        timing
    });

    return data;
}

export async function getAdminJobStatuses() {
    const { data } = await api.get("/admin/jobs/statuses");
    return data;
}

export async function setAdminJobEnabled(jobKey, isEnabled) {
    const { data } = await api.post(
        `/admin/jobs/${encodeURIComponent(jobKey)}/enabled`,
        { isEnabled }
    );

    return data;
}

export async function getAdminJobRunLogs(jobKey = "", take = 50) {
    const params = { take };

    if (jobKey) {
        params.jobKey = jobKey;
    }

    const { data } = await api.get("/admin/jobs/runs", {
        params
    });

    return data;
}

export async function runSystemMaintenanceNow() {
    const { data } = await api.post("/admin/jobs/system-maintenance/run-now");
    return data;
}

export async function runWebsiteRecoveryNow() {
    const { data } = await api.post("/admin/jobs/website-recovery/run-now");
    return data;
}

export async function processNextProjectModuleSync() {
    const { data } = await api.post("/admin/jobs/project-module-sync/process-next");
    return data;
}

export async function markStaleProjectModuleSyncRuns() {
    const { data } = await api.post("/admin/jobs/project-module-sync/mark-stale");
    return data;
}

export async function processNextWebsiteRedeploy() {
    const { data } = await api.post("/admin/jobs/website-redeploy/process-next");
    return data;
}

// =========================================================
// Custom Domains
// =========================================================

export async function markProjectDomainVerified(projectId, domainId, notes = "") {
    const res = await api.post(
        `/crm/projects/${projectId}/domains/${domainId}/mark-verified`,
        {
            notes
        }
    );

    return res.data;
}

export async function getProjectDomains(projectId) {
    const res = await api.get(`/crm/projects/${projectId}/domains`);
    return res.data;
}

export async function addProjectDomain(projectId, hostName) {
    const res = await api.post(`/crm/projects/${projectId}/domains`, {
        hostName
    });

    return res.data;
}

export async function checkProjectDomainDns(projectId, domainId) {
    const res = await api.post(
        `/crm/projects/${projectId}/domains/${domainId}/check-dns`
    );

    return res.data;
}

export async function activateProjectDomainAzure(
    projectId,
    domainId,
    markActiveAfterAzureBinding = false,
    notes = ""
) {
    const res = await api.post(
        `/crm/projects/${projectId}/domains/${domainId}/activate-azure`,
        {
            markActiveAfterAzureBinding,
            notes
        }
    );

    return res.data;
}

export async function setProjectPrimaryDomain(projectId, domainId) {
    const res = await api.post(
        `/crm/projects/${projectId}/domains/${domainId}/set-primary`
    );

    return res.data;
}

export async function deleteProjectDomain(projectId, domainId) {
    const res = await api.delete(
        `/crm/projects/${projectId}/domains/${domainId}`
    );

    return res.data;
}

// AI Providers API Calls
export async function getAiProviderRoutes() {
    const { data } = await api.get("/admin/ai-provider-routes");
    return data;
}

export async function saveAiProviderRoute(payload) {
    const { data } = await api.post("/admin/ai-provider-routes", payload);
    return data;
}

export async function deleteAiProviderRoute(id) {
    const { data } = await api.delete(`/admin/ai-provider-routes/${id}`);
    return data;
}

export async function testAiProviderRoute(
    routeId,
    systemPrompt,
    userPrompt,
    styleHint = "premium modern",
    layoutType = "hero"
) {
    const { data } = await api.post("/admin/ai-provider-routes/test", {
        routeId,
        systemPrompt,
        userPrompt,
        styleHint,
        layoutType
    });

    return data;
}
export async function getAiProviderDiscoveryCatalog() {
    const { data } = await api.get("/admin/ai-provider-discovery/catalog");
    return data;
}


// =========================================================
// Billing / Usage
// =========================================================

export async function getCurrentBillingUsage() {
    const response = await api.get("/billing/usage/current");
    return response.data;
}

export async function getAccountBillingUsage(accountId) {
    const response = await api.get(`/billing/usage/account/${accountId}`);
    return response.data;
}

export async function createBillingCheckout(planKey, accountId = null) {
    const response = await api.post("/billing/checkout", {
        planKey,
        accountId
    });

    return response.data;
}

export async function createBillingPortal(accountId = null) {
    const response = await api.post("/billing/portal", {
        accountId
    });

    return response.data;
}

export async function getCurrentBillingHistory() {
    const response = await api.get("/billing/history/current");
    return response.data;
}

export async function getAccountBillingHistory(accountId) {
    const response = await api.get(`/billing/history/account/${accountId}`);
    return response.data;
};

export const getWorkspaceUsers = async () => {
    const { data } = await api.get("/accounts/workspace-users");
    return data;
};

export const createWorkspaceUser = async (payload) => {
    const { data } = await api.post("/accounts/workspace-users", payload);
    return data;
};

export const updateWorkspaceUserRole = async (userId, roleKey) => {
    const { data } = await api.patch(`/accounts/workspace-users/${userId}/role`, {
        roleKey
    });
    return data;
};

export const disableWorkspaceUser = async (userId) => {
    const { data } = await api.post(`/accounts/workspace-users/${userId}/disable`);
    return data;
};

export const getModuleItems = async (projectId, moduleKey) => {
    const { data } = await api.get(`/crm/projects/${projectId}/modules/${moduleKey}/items`);
    return data;
};

export const getModuleItem = async (projectId, moduleKey, itemId) => {
    const { data } = await api.get(`/crm/projects/${projectId}/modules/${moduleKey}/items/${itemId}`);
    return data;
};

export const bulkImportModuleItems = async (projectId, moduleKey, payload) => {
    const { data } = await api.post(`/crm/projects/${projectId}/modules/${moduleKey}/bulk-import`, payload);
    return data;
};

export const bulkPublishModuleItems = async (projectId, moduleKey, itemIds, queueRedeploy = true) => {
    const { data } = await api.post(`/crm/projects/${projectId}/modules/${moduleKey}/items/publish`, {
        itemIds,
        queueRedeploy
    });
    return data;
};

export const bulkDeleteModuleItems = async (projectId, moduleKey, itemIds, queueRedeploy = true) => {
    const { data } = await api.delete(`/crm/projects/${projectId}/modules/${moduleKey}/items`, {
        data: { itemIds, queueRedeploy }
    });
    return data;
};

export const duplicateModuleItem = async (projectId, moduleKey, itemId) => {
    const { data } = await api.post(`/crm/projects/${projectId}/modules/${moduleKey}/items/${itemId}/duplicate`);
    return data;
};

export const reorderModuleItems = async (projectId, moduleKey, items, queueRedeploy = true) => {
    const { data } = await api.post(`/crm/projects/${projectId}/modules/${moduleKey}/items/reorder`, {
        items,
        queueRedeploy
    });
    return data;
};

export const bulkAssignModuleImages = async (projectId, moduleKey, items, queueRedeploy = true) => {
    const { data } = await api.post(`/crm/projects/${projectId}/modules/${moduleKey}/items/images`, {
        items,
        queueRedeploy
    });
    return data;
};

export const getModulesSummary = async (projectId) => {
    const { data } = await api.get(`/crm/projects/${projectId}/modules/summary`);
    return data;
};